<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to Compensation & Benefits Year List" @click="$router.push('/compensation/allocation')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="compensation" pageId="compensation-benefits" color="white"></help-link>
                     <v-btn dark icon title="Export to excel" @click="getControlReport" :loading="loading_xls">
                        <v-icon>cloud_download</v-icon>
                    </v-btn>
                    <v-btn v-if="!loading&&(hasEditRights||hasApproverRights)" dark icon small @click="showBatchCopy" :title="`Batch copy from previous year (${notCreatedCount} records)`"> 
                        <v-icon>copy_all</v-icon>
                    </v-btn>
                    <v-menu left v-if="!loading" v-model="menu_status" offset-y > 
                        <template v-slot:activator="{ on }">
                            <v-btn color="primary" dark v-on="on">
                                &nbsp;&nbsp;CY {{fiscalYear}}&nbsp;&nbsp;
                                <v-icon v-show="!menu_status">arrow_drop_down</v-icon>
                                <v-icon v-show="menu_status">arrow_drop_up</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-tile v-for="(item,index) in fiscal_years" :key="index" @click="setSelectedYear(item)" :title="`View ${item.fiscalYear} Compensation & Benefits`">
                                <v-list-tile-title small>CY {{item.fiscalYear}}</v-list-tile-title>
                            </v-list-tile>
                        </v-list>
                    </v-menu>
                </v-toolbar>
            </v-card>

            <!-- alerts -->
            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                Error encountered. Please contact support. Error details: {{errorMessage}}
            </v-alert>
            
            <!-- details -->
            <v-card class="elevation-1 mt-5"> 
                <v-toolbar flat height="45px">
                    <div :class="isMobile?'wp-50 mb-10':'wp-30 mb-10'">
                        <v-text-field v-model="search" append-icon="search" :label="searchLabel" single-line hide-details clearable></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? [] : headers"
                    :items="allocation_summary_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >
                    <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="headers.length" class="pa-1">
                                <v-card class="elevation-1" @click="viewDetails(props.item)">
                                    <v-toolbar flat height="42">
                                        <v-chip class="user-chip-container">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                            </v-avatar>
                                            {{props.item.firstName}} {{props.item.lastName}}
                                        </v-chip>
                                        <v-spacer></v-spacer>
                                        <team-chips :teams="props.item.teams"></team-chips>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="section-layout">
                                        <v-flex xs12 class="text-xs-left mt-5">
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Employment Status</div>
                                                    <div :class="commonfn.getStatusColor(props.item.employmentStatus)">{{commonfn.getStatusDescription(props.item.employmentStatus)}}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Pay Grade</div>
                                                    <div>{{props.item.payGrade}}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Salary Type</div>
                                                    <div>{{props.item.salaryType}}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Salary</div>
                                                    <div v-if="commonfn.getStatusDescription(props.item.allocationStatus) != 'Not Available'">
                                                        <span v-if="props.item.salaryType=='Monthly'">
                                                            ₱{{numFormat.formatNumber(props.item.monthlySalary)}} / mo
                                                        </span>
                                                        <span v-else>
                                                            ₱{{numFormat.formatNumber(props.item.hourlySalary)}} / hr
                                                        </span>
                                                    </div>
                                                    <div v-else>-</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Status</div>
                                                    <div :class="commonfn.getStatusColor(props.item.allocationStatus)">{{commonfn.getStatusDescription(props.item.allocationStatus)}}</div>                                                   
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>
                        
                        <!-- desktop view -->
                        <tr v-else @click="viewDetails(props.item)" title="Click to view and edit details" class="clickable">
                            <td>
                                <v-chip class="user-chip-container">
                                    <v-avatar>
                                        <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                    </v-avatar>
                                    {{props.item.firstName}} {{props.item.lastName}}
                                </v-chip>
                            </td>
                            <td>
                                <team-chips :teams="props.item.teams"></team-chips>
                            </td>
                            <td>
                                <b :class="commonfn.getStatusColor(props.item.employmentStatus)">{{commonfn.getStatusDescription(props.item.employmentStatus)}}</b>
                            </td>
                            <td>{{props.item.payGrade}}</td>
                            <td>{{props.item.salaryType}}</td>
                            <td>
                                <span v-if="commonfn.getStatusDescription(props.item.allocationStatus) != 'Not Available'">
                                    <span v-if="props.item.salaryType=='Monthly'">
                                        ₱{{numFormat.formatNumber(props.item.monthlySalary)}} / month
                                    </span>
                                    <span v-else>
                                        ₱{{numFormat.formatNumber(props.item.hourlySalary)}} / hour
                                    </span>
                                </span>
                            </td>
                            <td>
                                <b :class="commonfn.getStatusColor(props.item.allocationStatus)">{{commonfn.getStatusDescription(props.item.allocationStatus)}}</b>
                            </td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>

            <!-- Comments and Updates -->
            <v-layout row wrap>
                <v-flex xs12 sm6>
                    <!-- comments history component -->
                    <comments-history v-if="!loading" ref="commentsRef" :comments_data="commentsData"></comments-history>
                </v-flex>
                <v-flex xs12 sm6>
                    <update-history v-if="!loading" ref="updateHistoryRef" :page_query_id="pageQueryId"></update-history>
                </v-flex>
            </v-layout>
        </v-flex>

        <v-dialog v-model="batchCopyDialog" persistent width="900">
            <batch-copy ref="batchCopyComp" :batchCopyDialog.sync="batchCopyDialog" @send="getAllocationSummary(false)"></batch-copy>
        </v-dialog>

        <!-- status messaging component -->
        <status-messaging :page_query_id="pageQueryId" @refresh="updateHandler"></status-messaging>
    </v-layout> 
</template>

<script>

import allocationApi from '@/api/allocation'
import commonfn from '@/common/functions'   //for refactoring, create separate files/helpers/constanct files
import numFormat from '@/common/num_format'
import awsS3Helper from '@/helpers/aws-s3'
import rowsPerPageItems from '@/constants/rows-per-page'
import pageKeys from '@/common/pagekeys'
import notificationTypes from '@/common/notificationTypes'
import { hasEditRights, hasApproverRights } from '@/helpers/access-permissions'
import { getSort, getRowsPerPage, getSearchKeyword, updateRowsPerPage, updateSort, updateSearchKeyword } from '@/helpers/user-prefs'

export default {     
    name: 'Allocation',
    metaInfo: {
        title: 'Allocation',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Allocation' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'team-chips': () => import("@/components/Common/TeamsChip"),
        'batch-copy': () => import("@/components/Allocation/BatchCopyPrevious"),
        'status-messaging': () => import('@/components/Common/StatusMessaging'),
        'update-history': () => import('@/components/Common/UpdatesHistory'),
        'comments-history': () => import('@/components/Common/CommentsHistory')
    },   
    data () {
        const routeName = this.$route.name
        const savedSort = getSort(routeName)
        
        return {
            commonfn,
            numFormat,
            awsS3Helper,
            rowsPerPageItems,
            batchCopyDialog: false,
            paginationSync: {
                rowsPerPage: getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            search: getSearchKeyword(routeName),
            headers: [
                {text: 'Employee',align: 'left',value: 'lastName',width: '15%', sortable:true},
                {text: 'Teams',align: 'left',value: 'teams',width: '20%', sortable:true},          
                {text: 'Employment Status',align: 'left',value: 'employmentStatus',width: '10%', sortable:true},
                {text: 'Pay Grade',align: 'left',value: 'payGrade',width: '12%', sortable:true},
                {text: 'Salary Type',align: 'left',value: 'salaryType',width: '10%', sortable:true},
                {text: 'Salary',align: 'left',value: 'salaryValue',width: '15%', sortable:true},
                {text: 'Status',align: 'left',value: 'allocationStatus',width: '15%', sortable:true},
            ],
            fiscalYear: null,
            menu_status: false,
            error: false,
            errorMessage: '',
            loading: false,
            loading_xls: false,
            allocation_summary: [],
        }
    },
    methods: {  
        async setSelectedYear(data) {
            this.$router.push(`/compensation/allocation/${data.fiscalPeriodId}`)
            await this.getAllocationSummary(false)

            //update header titles
            this.$root.$emit('setHeaderTitles', {
                display: true,
                headerTitle: `${this.fiscalYear} Compensation & Benefits`,
                headerPageTitle: null
            })

            //update history 
            this.$refs.updateHistoryRef.getHistory()

            //refresh comments section
            this.$refs.commentsRef.invokeSubscribe()
            this.$refs.commentsRef.getCommentsHistory()
        },
        async showBatchCopy() {
            const targetData = {
                fiscalYear: this.fiscalYear,
                fiscalPeriodId: this.fiscalPeriodId
            }
            this.$refs.batchCopyComp.setData(targetData)
            this.batchCopyDialog = true
        },
        viewDetails(data) {
            this.$router.push(`/compensation/allocation/${this.fiscalPeriodId}/${data.userId}`)
        },
        async getControlReport() {
            try {
                this.loading_xls = true
                const response = await allocationApi.getSummaryXls(this.fiscalPeriodId)
                
                var blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
                var link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.download = `Compensation-${this.fiscalYear}.xlsx`
                link.click()
                this.loading_xls = false
            }catch(error){
                this.errorMessage = error
                this.error = true
            }
        },
        async getAllocationSummary(show_loading) {
            try {
                this.loading = show_loading
                const response = await allocationApi.getSummary(this.fiscalPeriodId)
                if (response && response.status == 200) {
                    this.allocation_summary = response.data.items
                    this.fiscalYear = this.allocation_summary[0].fiscalYear
                }
                this.loading = false
            }catch(error) {
                this.error = true
                this.errorMessage = error
            }
        },
        updateHandler(data) {
            //exclude the the device/user who initiates the update
            if (data!=this.$store.state.userId) this.getAllocationSummary(false)

            //invoke updateHistoryRef appendData 
            this.$refs.updateHistoryRef.getHistory()
        }
    },
    async created() {
        await this.getAllocationSummary(true)

        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: `${this.fiscalYear} Compensation & Benefits`,
            headerPageTitle: null
        })
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        },
        search(newVal) {
            updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        hasEditRights() {
            return hasEditRights(this.$router.currentRoute.path)
        },
        hasApproverRights() {
            return hasApproverRights(this.$router.currentRoute.path)
        },
        pageQueryId() {
            return `allocation_${this.fiscalPeriodId}`
        },
        fiscalPeriodId() {
            return this.$route.params.fiscalPeriodId
        },
        fiscal_years() {
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods
            }
            return [{fiscalYear: new Date().getFullYear()}]
        },
        allocation_summary_filtered() {
            var temp = this.allocation_summary

            if (this.search) {
                const searchLower = this.search.toLowerCase()

                temp = temp.filter(item => {
                    const firstNameMatch = item.firstName.toLowerCase().includes(searchLower)
                    const lastNameMatch = item.lastName.toLowerCase().includes(searchLower)
                    const salaryTypeMatch = item.salaryType.toLowerCase().includes(searchLower)
                    const teamsMatch = Array.isArray(item.teams)
                        ? item.teams.join(', ').toLowerCase().includes(searchLower)
                        : false

                    return firstNameMatch || lastNameMatch || salaryTypeMatch || teamsMatch
                })
            }

            // 💰 Add computed salary fields for sorting/display
            temp = temp.map(e => {
                const salaryValue =
                e.salaryType === 'Monthly'
                    ? (e.monthlySalary || 0)
                    : (e.hourlySalary || 0)

                return {
                ...e,
                    salaryValue,
                    salaryDisplay:
                        e.salaryType === 'Monthly'
                        ? `₱${this.numFormat.formatNumber(salaryValue)} / month`
                        : `₱${this.numFormat.formatNumber(salaryValue)} / hour`,
                    }
            })

            return temp
        },
        processedCount() {
            return this.allocation_summary.filter(item => {
                    return item.allocationStatus == commonfn.getStatusCode('Created') ||
                        item.allocationStatus == commonfn.getStatusCode('Under Revision') ||
                        item.allocationStatus == commonfn.getStatusCode('Approved')
                }).length
        },
        notCreatedCount() {
            return this.allocation_summary.filter(item => {
                    return item.allocationStatus == commonfn.getStatusCode('Not Available') &&
                            item.employmentStatus != commonfn.getStatusCode('Resigned') &&
                            item.employmentStatus != commonfn.getStatusCode('Terminated')
                }).length
        },
        commentsData() {
            if (!this.loading) {
                // this is for refactor, some fields are not required in comments component
                return {
                    pageQueryId: this.pageQueryId,
                    pageItemCreatedBy: null, 
                    pageLink: this.$router.currentRoute.path,
                    pageKey: pageKeys.allocation,
                    notificationTypeId: notificationTypes.allocation
                }
            }
        },
        searchLabel() {
            return this.isMobile ? 'Search' : 'Search team, employee, salary type'
        },
    }
}
</script>
<style scoped>
.section-layout {
    padding:5px 23px 5px 23px;
}
</style>